'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { AVATARS, AVATAR_STORAGE_KEY } from '@/lib/avatars';
import LoginModal from '@/components/LoginModal';

const navLinks = [
  { label: 'Exams', href: '/exams' },
  { label: 'Test Series', href: '/exams' },
  { label: 'Roadmap', href: '/roadmap' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'About', href: '/about' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const { user, loading, signOut } = useAuth();
  const router = useRouter();
  const [selectedAvatarId, setSelectedAvatarId] = useState<string | null>(null);

  useEffect(() => {
    const loadAvatar = () => {
      const stored = localStorage.getItem(AVATAR_STORAGE_KEY);
      setSelectedAvatarId(stored || null);
    };
    loadAvatar();
    window.addEventListener('storage', loadAvatar);
    const interval = setInterval(loadAvatar, 1000);
    return () => {
      window.removeEventListener('storage', loadAvatar);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      setUserMenuOpen(false);
      router?.push('/home');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Sign out error:', err);
    }
  };

  const userInitial = user?.user_metadata?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U';
  const userName = user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'Student';

  const currentAvatar = AVATARS?.find((a) => a?.id === selectedAvatarId);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-navy py-3' : 'bg-navy py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-6">
        {/* Logo */}
        <Link href="/home" className="flex items-center gap-2.5 shrink-0 group">
          <div className="w-8 h-8 rounded-xl bg-saffron flex items-center justify-center">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 2L3 6v6l6 4 6-4V6L9 2z" fill="#0F2044" />
              <path d="M9 5.5L5.5 8v3L9 13l3.5-2V8L9 5.5z" fill="#F5A623" opacity="0.6" />
            </svg>
          </div>
          <span className="font-display text-xl font-bold text-slate tracking-tight">
            Minikwa
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
          {navLinks?.map((link) => (
            <Link
              key={link?.href + link?.label}
              href={link?.href}
              className="anchor-link px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap text-slate/70 hover:text-slate"
            >
              {link?.label}
            </Link>
          ))}
        </nav>

        {/* CTA */}
        <div className="flex items-center gap-3 shrink-0">
          {loading ? (
            <div className="w-8 h-8 rounded-full bg-white/10 animate-pulse" />
          ) : user ? (
            /* Authenticated: User Avatar + Dropdown (all nav + actions inside dropdown) */
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 transition-colors"
              >
                {currentAvatar ? (
                  <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0">
                    {currentAvatar?.svg}
                  </div>
                ) : (
                  <div className="w-7 h-7 rounded-full bg-saffron flex items-center justify-center text-navy text-xs font-bold">
                    {userInitial}
                  </div>
                )}
                <span className="hidden md:block text-sm text-slate/80 max-w-[100px] truncate">{userName}</span>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className={`text-slate/50 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}>
                  <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-navy-light border border-white/10 rounded-xl py-1 z-50">
                  <div className="px-4 py-2 border-b border-white/10">
                    <p className="text-sm font-medium text-slate truncate">{userName}</p>
                    <p className="text-xs text-slate/50 truncate">{user?.email}</p>
                  </div>
                  {/* Nav links — visible on mobile/tablet only */}
                  <div className="lg:hidden border-b border-white/10 py-1">
                    {navLinks?.map((link) => (
                      <Link
                        key={link?.href + link?.label}
                        href={link?.href}
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full text-left px-4 py-2.5 text-sm text-slate/70 hover:text-slate hover:bg-white/5 transition-colors flex items-center gap-2"
                      >
                        {link?.label}
                      </Link>
                    ))}
                  </div>
                  <Link
                    href="/dashboard"
                    onClick={() => setUserMenuOpen(false)}
                    className="w-full text-left px-4 py-2.5 text-sm text-slate/70 hover:text-slate hover:bg-white/5 transition-colors flex items-center gap-2"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
                      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
                    </svg>
                    My Dashboard
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-2.5 text-sm text-slate/70 hover:text-slate hover:bg-white/5 transition-colors flex items-center gap-2"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
                    </svg>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Unauthenticated: Login + Start Practising */
            <>
              <button
                onClick={() => setLoginModalOpen(true)}
                className="hidden md:flex items-center px-4 py-2 text-sm font-medium text-slate/70 hover:text-slate transition-colors"
              >
                Log in
              </button>
              <button
                onClick={() => setLoginModalOpen(true)}
                className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-saffron text-navy rounded-full text-sm font-bold hover:bg-saffron-light transition-all duration-200 hover:scale-105 active:scale-95"
              >
                Start Practising
              </button>
              {/* Hamburger only for unauthenticated users on mobile */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden text-slate p-2 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Toggle menu"
              >
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  {mobileOpen ? (
                    <path d="M5 5l12 12M17 5L5 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  ) : (
                    <>
                      <path d="M3 6h16M3 11h16M3 16h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </>
                  )}
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mobile menu — only for unauthenticated users */}
      {mobileOpen && !user && (
        <div className="lg:hidden bg-navy-light border-t border-white/10 px-6 py-4">
          <nav className="flex flex-col gap-2">
            {navLinks?.map((link) => (
              <Link
                key={link?.href + link?.label}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className="text-slate/80 hover:text-saffron py-2 text-sm font-medium transition-colors"
              >
                {link?.label}
              </Link>
            ))}
            <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-white/10">
              <button
                onClick={() => { setMobileOpen(false); setLoginModalOpen(true); }}
                className="text-center py-3 border border-white/20 text-slate rounded-full font-medium text-sm hover:bg-white/10 transition-colors"
              >
                Log in
              </button>
              <button
                onClick={() => { setMobileOpen(false); setLoginModalOpen(true); }}
                className="text-center py-3 bg-saffron text-navy rounded-full font-bold text-sm"
              >
                Start Practising
              </button>
            </div>
          </nav>
        </div>
      )}

      <LoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </header>
  );
}